import { Box, Button, Chip, Stack, type SxProps, Typography } from '@mui/material';
import { alpha, styled, type Theme } from '@mui/material/styles';
import type {
  CreatePartnerWithRelatedDataError,
  CreatePartnerWithRelatedDataInput,
} from '@provision/entity-types';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import { FormPhoneInput } from 'src/components/Forms/FormPhoneInput/form-phone-input';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import {
  DOMAIN_VALIDATION_REGEX,
  validateEmail,
  validatePhone,
} from 'src/components/Forms/validations';
import { timeZones } from 'src/constants/timezone';
import {
  PartnerGroupOption,
  useAssignablePartnerGroupsOptions,
} from 'src/graphql/entities/group-rules/partner-group';

export enum CreatePartnerFormField {
  PartnerId = 'partnerId',
  BusinessName = 'businessName',
  Address = 'address',
  City = 'city',
  State = 'state',
  Zip = 'zip',
  TimeZone = 'timeZone',
  WebsiteUrl = 'websiteUrl',
  SupportEmail = 'supportEmail',
  SupportPhone = 'supportPhone',
  NewBrandName = 'newBrandName',
  ZendeskDomain = 'zendeskDomain',
  UsersEmailDomain = 'usersEmailDomain',
  PartnerGroupIdsToAssign = 'partnerGroupIdsToAssign',
  CurrentPartner = 'currentPartner',
  Brands = 'brands',
}

export interface CreatePartnerFormValues {
  [CreatePartnerFormField.PartnerId]: string;
  [CreatePartnerFormField.BusinessName]: string;
  [CreatePartnerFormField.Address]: string;
  [CreatePartnerFormField.City]: string;
  [CreatePartnerFormField.State]: string;
  [CreatePartnerFormField.Zip]: string;
  [CreatePartnerFormField.TimeZone]: string;
  [CreatePartnerFormField.WebsiteUrl]: string;
  [CreatePartnerFormField.SupportEmail]: string;
  [CreatePartnerFormField.SupportPhone]: string;
  [CreatePartnerFormField.NewBrandName]: string;
  [CreatePartnerFormField.ZendeskDomain]: string;
  [CreatePartnerFormField.UsersEmailDomain]: string;
  [CreatePartnerFormField.PartnerGroupIdsToAssign]: PartnerGroupOption[];
  [CreatePartnerFormField.CurrentPartner]: 'yes' | 'no';
  [CreatePartnerFormField.Brands]: { value: string }[];
}

const DEFAULT_BRANDS = [{ value: 'Residential' }, { value: 'Commercial' }];

const createPartnerZipFieldSx: SxProps<Theme> = {
  maxWidth: { xs: '100%', sm: 'calc((100% - 16px) / 2)' },
  width: '100%',
};

const createPartnerSegmentHeadingSx: SxProps<Theme> = {
  display: 'block',
  marginTop: 0.5,
  marginBottom: 1.5,
};

const createPartnerAddSegmentButtonSx: SxProps<Theme> = {
  alignSelf: { xs: 'stretch', sm: 'flex-end' },
  minWidth: { xs: '100%', sm: '10rem' },
  whiteSpace: 'nowrap',
};

const createPartnerSegmentErrorTextSx: SxProps<Theme> = {
  display: 'block',
  marginTop: 0.5,
};

const createPartnerSegmentInputSx: SxProps<Theme> = {
  flex: { xs: '1 1 auto', sm: '1 1 22rem' },
};

const createPartnerPermissionsHeadingSx: SxProps<Theme> = {
  display: 'block',
  marginTop: 0.5,
  marginBottom: 1.5,
};

const CreatePartnerSegmentChip = styled(Chip, {
  name: 'CreatePartnerSegmentChip',
})(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.14),
  borderRadius: theme.spacing(0.75),
  color: theme.palette.primary.main,
  fontWeight: 500,
  '& .MuiChip-deleteIcon': {
    color: theme.palette.primary.main,
  },
}));

const ADDRESS_FIELD_MAX_LENGTH = 255;
const BUSINESS_NAME_MAX_LENGTH = 255;
const BRAND_NAME_MAX_LENGTH = 255;
const ZENDESK_DOMAIN_MAX_LENGTH = 250;
const USERS_EMAIL_DOMAIN_MAX_LENGTH = 250;
const STATE_FIELD_MAX_LENGTH = 2;

export function getCreatePartnerDefaultValues(): CreatePartnerFormValues {
  return {
    [CreatePartnerFormField.PartnerId]: '',
    [CreatePartnerFormField.BusinessName]: '',
    [CreatePartnerFormField.Address]: '',
    [CreatePartnerFormField.City]: '',
    [CreatePartnerFormField.State]: '',
    [CreatePartnerFormField.Zip]: '',
    [CreatePartnerFormField.TimeZone]: '',
    [CreatePartnerFormField.WebsiteUrl]: '',
    [CreatePartnerFormField.SupportEmail]: '',
    [CreatePartnerFormField.SupportPhone]: '',
    [CreatePartnerFormField.NewBrandName]: '',
    [CreatePartnerFormField.ZendeskDomain]: '',
    [CreatePartnerFormField.UsersEmailDomain]: '',
    [CreatePartnerFormField.PartnerGroupIdsToAssign]: [],
    [CreatePartnerFormField.CurrentPartner]: 'yes',
    [CreatePartnerFormField.Brands]: DEFAULT_BRANDS.map((brand) => ({ ...brand })),
  };
}

export function useCreatePartnerForm({
  formMethods,
  isSubmitting = false,
}: {
  formMethods: ReturnType<typeof useForm<CreatePartnerFormValues>>;
  isSubmitting?: boolean;
}) {
  const {
    control,
    clearErrors,
    formState: { errors },
    setValue,
    watch,
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: CreatePartnerFormField.Brands,
  });
  const { options: assignablePartnerGroups, loading: assignablePartnerGroupsLoading } =
    useAssignablePartnerGroupsOptions({
      fetchPolicy: 'cache-first',
    });

  const newBrandName = watch(CreatePartnerFormField.NewBrandName);

  const handleAddBrand = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    const trimmedValue = newBrandName.trim();
    if (!trimmedValue) {
      return;
    }

    clearErrors(CreatePartnerFormField.Brands);
    append({ value: trimmedValue });
    setValue(CreatePartnerFormField.NewBrandName, '');
  }, [append, clearErrors, isSubmitting, newBrandName, setValue]);

  const handleRemoveBrand = useCallback(
    (index: number) => {
      if (fields.length <= 1) {
        return;
      }

      remove(index);
    },
    [fields.length, remove],
  );

  return useMemo(
    () => (
      <>
        <FormTextField
          fullWidth
          type="textfield"
          name={CreatePartnerFormField.PartnerId}
          label="Partner ID"
          required="Partner ID is required"
          disabled={isSubmitting}
          textFieldProps={{
            required: true,
          }}
        />

        <FormTextField
          fullWidth
          type="textfield"
          name={CreatePartnerFormField.BusinessName}
          label="Business name"
          required="Business name is required"
          disabled={isSubmitting}
          textFieldProps={{
            inputProps: { maxLength: BUSINESS_NAME_MAX_LENGTH },
            required: true,
          }}
        />

        <FormTextField
          fullWidth
          type="textfield"
          name={CreatePartnerFormField.Address}
          label="Address"
          disabled={isSubmitting}
          textFieldProps={{
            inputProps: { maxLength: ADDRESS_FIELD_MAX_LENGTH },
          }}
        />

        <Stack alignItems="flex-start" direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormTextField
            fullWidth
            type="textfield"
            name={CreatePartnerFormField.City}
            label="City"
            disabled={isSubmitting}
            textFieldProps={{
              inputProps: { maxLength: ADDRESS_FIELD_MAX_LENGTH },
            }}
          />

          <FormTextField
            fullWidth
            type="textfield"
            name={CreatePartnerFormField.State}
            label="State"
            disabled={isSubmitting}
            textFieldProps={{
              inputProps: { maxLength: STATE_FIELD_MAX_LENGTH },
            }}
          />
        </Stack>

        <FormTextField
          type="textfield"
          name={CreatePartnerFormField.Zip}
          label="Zip"
          disabled={isSubmitting}
          sx={createPartnerZipFieldSx}
          textFieldProps={{
            inputProps: { maxLength: ADDRESS_FIELD_MAX_LENGTH },
          }}
        />

        <FormAutocomplete
          fullWidth
          type="autocomplete"
          name={CreatePartnerFormField.TimeZone}
          label="Timezone"
          options={[...timeZones]}
          disabled={isSubmitting}
        />

        <FormTextField
          fullWidth
          type="textfield"
          name={CreatePartnerFormField.WebsiteUrl}
          label="Website URL"
          disabled={isSubmitting}
        />

        <FormTextField
          fullWidth
          type="textfield"
          name={CreatePartnerFormField.SupportEmail}
          label="Support email"
          disabled={isSubmitting}
        />

        <FormPhoneInput
          type="phone"
          name={CreatePartnerFormField.SupportPhone}
          label="Support phone"
          disabled={isSubmitting}
          sx={{ width: '100%' }}
          textFieldProps={{
            fullWidth: true,
            disabled: isSubmitting,
          }}
        />

        <FormTextField
          fullWidth
          type="textfield"
          name={CreatePartnerFormField.ZendeskDomain}
          label="Domain"
          disabled={isSubmitting}
          textFieldProps={{
            inputProps: { maxLength: ZENDESK_DOMAIN_MAX_LENGTH },
          }}
        />

        <FormTextField
          fullWidth
          type="textfield"
          name={CreatePartnerFormField.UsersEmailDomain}
          label="Users Email Domain"
          disabled={isSubmitting}
          showErrorText
          textFieldProps={{
            inputProps: { maxLength: USERS_EMAIL_DOMAIN_MAX_LENGTH },
          }}
        />

        <Box>
          <Typography variant="subtitle2">Client segments *</Typography>
          <Typography
            color="text.secondary"
            sx={createPartnerSegmentHeadingSx}
            variant="caption"
          >
            At least one required
          </Typography>

          <Stack direction="row" flexWrap="wrap" gap={1} mb={1.5}>
            {fields.map((field, index) => (
              <CreatePartnerSegmentChip
                key={field.id}
                label={field.value}
                onDelete={fields.length > 1 ? () => handleRemoveBrand(index) : undefined}
              />
            ))}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <FormTextField
              fullWidth
              type="textfield"
              name={CreatePartnerFormField.NewBrandName}
              label="Enter segment name"
              disabled={isSubmitting}
              sx={createPartnerSegmentInputSx}
              textFieldProps={{
                inputProps: { maxLength: BRAND_NAME_MAX_LENGTH },
                placeholder: 'Enter segment name',
                onKeyDown: (event) => {
                  if (event.key !== 'Enter') {
                    return;
                  }

                  event.preventDefault();
                  handleAddBrand();
                },
              }}
            />
            <Button
              disabled={isSubmitting}
              onClick={handleAddBrand}
              startIcon={<DynamicIcon aria-hidden name="add" />}
              sx={createPartnerAddSegmentButtonSx}
            >
              Add segment
            </Button>
          </Stack>

          {typeof errors[CreatePartnerFormField.Brands]?.message === 'string' ? (
            <Typography color="error" sx={createPartnerSegmentErrorTextSx} variant="caption">
              {errors[CreatePartnerFormField.Brands]?.message}
            </Typography>
          ) : null}
        </Box>

        <Box>
          <Typography variant="subtitle2">Partner Permissions</Typography>
          <Typography
            color="text.secondary"
            sx={createPartnerPermissionsHeadingSx}
            variant="caption"
          >
            Optional partner groups to assign during partner creation
          </Typography>

          <FormAutocomplete
            fullWidth
            type="autocomplete"
            name={CreatePartnerFormField.PartnerGroupIdsToAssign}
            label="Partner groups"
            data-testid="create-partner-partner-groups"
            options={assignablePartnerGroups}
            loading={assignablePartnerGroupsLoading}
            multiple
            disabled={isSubmitting}
            autocompleteProps={{
              noOptionsText: 'No partner groups available',
              disableCloseOnSelect: true,
            }}
          />
        </Box>
      </>
    ),
    [
      assignablePartnerGroups,
      assignablePartnerGroupsLoading,
      errors,
      fields,
      handleAddBrand,
      handleRemoveBrand,
      isSubmitting,
    ],
  );
}

export function validateCreatePartnerForm(
  values: CreatePartnerFormValues,
  setError: ReturnType<typeof useForm<CreatePartnerFormValues>>['setError'],
) {
  let hasError = false;

  if (!values[CreatePartnerFormField.PartnerId].trim()) {
    setError(CreatePartnerFormField.PartnerId, {
      type: 'manual',
      message: 'Partner ID is required',
    });
    hasError = true;
  } else {
    const parsedPartnerId = Number(values[CreatePartnerFormField.PartnerId]);
    if (!Number.isInteger(parsedPartnerId) || parsedPartnerId <= 0) {
      setError(CreatePartnerFormField.PartnerId, {
        type: 'manual',
        message: 'Partner ID must be a positive number',
      });
      hasError = true;
    }
  }

  if (!values[CreatePartnerFormField.BusinessName].trim()) {
    setError(CreatePartnerFormField.BusinessName, {
      type: 'manual',
      message: 'Business name is required',
    });
    hasError = true;
  }

  const websiteUrl = values[CreatePartnerFormField.WebsiteUrl].trim();
  if (websiteUrl && !/^https?:\/\//.test(websiteUrl)) {
    setError(CreatePartnerFormField.WebsiteUrl, {
      type: 'manual',
      message: 'Website must start with http:// or https://',
    });
    hasError = true;
  }

  const supportEmail = values[CreatePartnerFormField.SupportEmail].trim();
  if (supportEmail && !validateEmail(supportEmail)) {
    setError(CreatePartnerFormField.SupportEmail, {
      type: 'manual',
      message: 'Invalid email address',
    });
    hasError = true;
  }

  const supportPhone = values[CreatePartnerFormField.SupportPhone].trim();
  if (supportPhone && !validatePhone(supportPhone)) {
    setError(CreatePartnerFormField.SupportPhone, {
      type: 'manual',
      message: 'Invalid phone number',
    });
    hasError = true;
  }

  const usersEmailDomain = values[CreatePartnerFormField.UsersEmailDomain].trim();
  if (usersEmailDomain && !DOMAIN_VALIDATION_REGEX.test(usersEmailDomain)) {
    setError(CreatePartnerFormField.UsersEmailDomain, {
      type: 'manual',
      message: 'Users Email Domain must be a valid domain',
    });
    hasError = true;
  }

  return !hasError;
}

export function toCreatePartnerPayload(
  values: CreatePartnerFormValues,
): CreatePartnerWithRelatedDataInput {
  const payload: CreatePartnerWithRelatedDataInput = {
    id: Number(values[CreatePartnerFormField.PartnerId]),
    businessName: values[CreatePartnerFormField.BusinessName].trim(),
    address: trimOptionalValue(values[CreatePartnerFormField.Address]),
    city: trimOptionalValue(values[CreatePartnerFormField.City]),
    state: trimOptionalValue(values[CreatePartnerFormField.State]),
    zip: trimOptionalValue(values[CreatePartnerFormField.Zip]),
    timeZone: trimOptionalValue(values[CreatePartnerFormField.TimeZone]),
    websiteUrl: trimOptionalValue(values[CreatePartnerFormField.WebsiteUrl]),
    supportEmail: trimOptionalValue(values[CreatePartnerFormField.SupportEmail]),
    supportPhone: trimOptionalValue(values[CreatePartnerFormField.SupportPhone]),
    zendeskDomain: trimOptionalValue(values[CreatePartnerFormField.ZendeskDomain]),
    usersEmailDomain: trimOptionalValue(values[CreatePartnerFormField.UsersEmailDomain]),
    currentPartner: values[CreatePartnerFormField.CurrentPartner],
    brandsToCreate: values[CreatePartnerFormField.Brands]
      .map((brand) => brand.value.trim())
      .filter(Boolean),
  };

  const selectedPartnerGroupIds = values[CreatePartnerFormField.PartnerGroupIdsToAssign]
    .map((partnerGroup) => partnerGroup.value.trim())
    .filter(Boolean);

  if (selectedPartnerGroupIds.length) {
    payload.partnerGroupIdsToAssign = selectedPartnerGroupIds;
  }

  return payload;
}

export function mapCreatePartnerResponseErrors(
  errors: CreatePartnerWithRelatedDataError[],
  setError: ReturnType<typeof useForm<CreatePartnerFormValues>>['setError'],
  setSubmitError: (value: string | null) => void,
) {
  if (errors[0]?.message) {
    setSubmitError(errors[0].message);
  }

  for (const error of errors) {
    const message = error.message;
    const lowerCaseMessage = message.toLowerCase();

    if (message.includes('businessName')) {
      setError(CreatePartnerFormField.BusinessName, { type: 'manual', message });
      continue;
    }

    if (message.includes('websiteUrl')) {
      setError(CreatePartnerFormField.WebsiteUrl, { type: 'manual', message });
      continue;
    }

    if (message.includes('supportEmail')) {
      setError(CreatePartnerFormField.SupportEmail, { type: 'manual', message });
      continue;
    }

    if (
      message.includes('usersEmailDomain') ||
      lowerCaseMessage.includes('users email domain')
    ) {
      setError(CreatePartnerFormField.UsersEmailDomain, { type: 'manual', message });
      continue;
    }

    if (
      /\b(?:id|partner id|params\.id)\b/i.test(message) ||
      lowerCaseMessage.includes('partner with this id already exists')
    ) {
      setError(CreatePartnerFormField.PartnerId, { type: 'manual', message });
      continue;
    }

    if (lowerCaseMessage.includes('zendesk') || lowerCaseMessage.includes('domain')) {
      setError(CreatePartnerFormField.ZendeskDomain, { type: 'manual', message });
      continue;
    }

    if (message.includes('brandsToCreate') || lowerCaseMessage.includes('brand')) {
      setError(CreatePartnerFormField.Brands, { type: 'manual', message });
      continue;
    }

    if (lowerCaseMessage.includes('partner group')) {
      setError(CreatePartnerFormField.PartnerGroupIdsToAssign, { type: 'manual', message });
    }
  }
}

function trimOptionalValue(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}
